import { Documents } from "@/components/documents/Documents.tsx";
import { DocumentFolderEditModal } from "@/components/documents/widgets/DocumentFolderEditModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import NotFound from "@/pages/NotFound.tsx";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { useDocumentFoldersList } from "@/queries/use-documents.query.ts";
import { ApiFolderType, folderParamKeys } from "@/types/document.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  FontAwesomeIcon,
  Page,
  Search,
  Stack,
  useDebouncedSearchParam,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export default function DocsPage() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { canIEdit } = usePermissions();
  const [searchParams] = useSearchParams();
  const {
    value: searchValue,
    setValue: setSearchValue,
    clear: clearSearch,
  } = useDebouncedSearchParam({ paramName: "docs_search", delay: 300 });
  useTitle(t("core:documents"));

  const { data: appConfig, isLoading: isConfigLoading } = useAppConfig();
  const { data: orgFolders, isLoading: isOrgFoldersLoading } = useDocumentFoldersList(
    ApiFolderType.organization
  );

  const canCreateFolder = canIEdit(ApiPermission.settings_document_folders);
  const hasOpenFolder = !!searchParams.get(folderParamKeys[ApiFolderType.organization]);

  if (!isConfigLoading && !appConfig?.ui.settings.can_view_organization_folders) {
    return <NotFound />;
  }

  return (
    <Page>
      <Page.Header title={t("core:organization_documents")}>
        {!hasOpenFolder && canCreateFolder && (
          <Button
            variant={"primary"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-folder-plus"} />}
            onClick={() => {
              openModal(DocumentFolderEditModal, {
                folder: null,
                type: ApiFolderType.organization,
              });
            }}
          >
            {t("core:create_folder")}
          </Button>
        )}
      </Page.Header>
      <Page.Content>
        {!hasOpenFolder && (
          <Search
            value={searchValue}
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            type={"text"}
            placeholder={t("common:search")}
            clearable={true}
            onClear={clearSearch}
          />
        )}
        <Stack className={"pt-8 px-16 gap-16"}>
          <Documents
            folders={orgFolders || []}
            isLoading={isOrgFoldersLoading}
            foldersType={ApiFolderType.organization}
            showManageFolders
            enableSearch
            hideListHeader
          />
        </Stack>
      </Page.Content>
    </Page>
  );
}
